import { Component, Input, OnDestroy, OnInit, Output, EventEmitter, TemplateRef } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { PageLayoutService } from 'src/shared/services/page-layout.service';
import { Constants } from '../utilities/constants.util';
import { HuronEntityAdminPage } from './entity-admin.component';
import { MappingEntityInterface } from './mapping-entity.interface';
import { MappingServiceInterface } from './mapping-service.interface';

@Component({
  selector: 'hhsg-mapping',
  templateUrl: 'mapping-page.component.html',
  styleUrls: ['mapping-page.component.scss']
})

export class HuronMappingPageComponent extends HuronEntityAdminPage implements OnDestroy, OnInit {
    @Input() entityName: string;
    @Input() pageName: string;
    @Input() searchByText: string;
    @Input() sortOptions: string[];
    @Input() mappingService: MappingServiceInterface;
    @Input() getDisplayName: Function;
    @Input() isSelected: Function;
    @Input() mappingForm: NgForm;
    @Input() mappingDetailTemplate: TemplateRef<HTMLElement>;

	@Output() onSelectionEvent: EventEmitter<string> = new EventEmitter<string>();
	@Output() isActiveEvent: EventEmitter<any> = new EventEmitter();
    
    mappings: MappingEntityInterface[];
    unmappedItemCount: number = 0;
    unmappedOnly: boolean;
    private saveMappingSubject: Subject<any> = new Subject();

    constructor(
        protected _pageLayout: PageLayoutService) { 
        super(_pageLayout);

        this.saveMappingSubject
        .pipe(debounceTime(Constants.DebounceTime))
        .subscribe(async () => {
            await this.saveMapping();
            }
        );
    }

    ngOnDestroy() {
		super.cleanUp();
    }

    async ngOnInit() {
        this.unmappedOnly = false;

        super.initialize(
            this.pageName,
            this.sortOptions,
            () => this.setCurrentSelectionLabel(),
            () => { return "Remove"; });           
    }

    private async saveMapping(): Promise<any> {
        if (!this.selection) { 
            return; 
        }
        
        let result = await this.mappingService.saveMapping(this.selection);
        if (result.isMapped) {          
            await super.refreshAdminList(true, true);
        
            this.resetForm();
        }
    }

    protected async adminActionSave(): Promise<any> {
        this.saveMappingSubject.next();
    }
    
    protected async adminListAppend(): Promise<any> {
        let result = await this.mappingService.getItemsToMap( 
            this.offset, 
            this.itemsPerPage, 
            this.searchText, 
            this.selectedFilter, 
            this.getSortOrderString(),
            this.unmappedOnly);
        this.mappings = this.mappings.concat(result);
    }

    protected async adminActionRefresh(): Promise<any> {
        super.setErrorMessage("");
        if (this.onSelectionEvent) {
            this.onSelectionEvent.emit('onSelection');
        }
    }

    protected async adminListRefresh(newFirst: boolean): Promise<any> {
        let result = await this.mappingService.getItemsToMap( 
            this.offset, 
            this.itemsPerPage, 
            this.searchText, 
            this.selectedFilter, 
            this.getSortOrderString(),
            this.unmappedOnly);
        this.mappings = result;
    
        this.resetForm();

        this.unmappedItemCount = await this.mappingService.getUnmappedItemCount();

        return result;
    }
  
    onCancel() {
		super.onCancel();
		this.resetForm();
    }

    async toggleUnmappedOnly() {
        this.unmappedOnly = !this.unmappedOnly;
        super.refreshAdminList(false, false);
    }

    async refreshCostCenterMappingList(mostRecent: boolean): Promise<any[]> {    
        let result = await this.mappingService.getItemsToMap( 
            this.offset, 
            this.itemsPerPage, 
            this.searchText, 
            this.selectedFilter, 
            this.getSortOrderString(),
            this.unmappedOnly);
        this.mappings = result;

        this.resetForm();

        this.unmappedItemCount = await this.mappingService.getUnmappedItemCount();

        return result;
  }

    resetForm() {
		if (!this.mappingForm) {
			return;
        }

		Object.keys(this.mappingForm.controls).forEach(control => {
			this.mappingForm.controls[control].markAsPristine();
		});
	}
    
    setCurrentSelectionLabel(): string {
        if (!this.selection) {
            return "";
        }
        return this.getDisplayName(this.selection);
    }

    getIsActive(mapping: MappingEntityInterface): boolean {
        if (this.isSelected != null) {
            return this.isSelected(mapping, this.selection);
        }
        return this.selection != undefined && this.selection != null && mapping.id == this.selection.id;
    }
    
    isSaveButtonDisabled(): boolean {
        return !this.mappingForm || !this.mappingForm.form.valid || !this.mappingForm.dirty || !this.saveButtonEnabled;
    }

    onSelection(selected: any) {
        super.onSelection(selected);
        if (this.mappingForm && this.mappingForm.dirty) {
            this.resetForm();
        }
    }
}
